import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { GenericResponse, Registration, RegistrationUpdateRequest, Event, RegistrationCreateRequest } from '../domain/registrator';
import { MessageService } from './message.service';
const CACHE_SIZE = 1;

export interface ConfirmationResponse {
  registrationId: number;
  attendeeId: number;
  confirmationDate: string; // LocalDate serialized as ISO-8601
}


@Injectable({
  providedIn: 'root'
})
export class RegistrationService {
  endpoint: string = environment.api_url;
  headers = new HttpHeaders().set('Content-Type', 'application/json');
  private cache$!: Observable<Array<Event>>;
  private cacheDirty: boolean = true;

  constructor(private http: HttpClient, private messageService: MessageService) { }

  getEventRegistrations(eventShortName: string, filter?: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    // const statuses = Object.keys(RegistrationStatus)
    //   .filter(key => isNaN(Number(key)) && key !== 'DELETED'); // Exclude 'DELETED' and numeric keys
    // const params = new HttpParams().set('statusFilter', statuses.join(','));

    const params = {};

    return this.http.get<Registration[]>(`${this.endpoint}/events/${eventShortName}/registrations`, { params, headers }).pipe(
      // Map the response array, converting date strings to Date objects.
      // Dates are sent by backend as UTC ISO8601 strings, without timezone
      // It may sound like a hack, but adding "Z" at the end makes it a UTC timestamp 
      map((response: Registration[]) => {
        return response.map(item => ({
          ...item,
          event: new Event(
            item.event.id,
            item.event.name,
            item.event.shortName,
            item.event.pageTitle,
            item.event.headerText,
            item.event.invoicePrefix,
            item.event.date ? item.event.date : null,
            item.event.endDate ? item.event.endDate : null,
            item.event.url,
            item.event.location,
            item.event.shortLocation,
            item.event.logoFileName,
            item.event.freeEvent,
            item.event.allowMultipleRegistrations,
            item.event.usesPromoCodes,
            item.event.oneFreeFor,
            item.event.vatRate,
            item.event.earlyBirdDeadline ? item.event.earlyBirdDeadline : null,
            item.event.registrationOpenDate ? item.event.registrationOpenDate : null,
            item.event.registrationCloseDate ? item.event.registrationCloseDate : null,
            item.event.fees,
            item.event.disclaimers,
            item.event.paymentConditions,
            item.event.contactEmail,
            item.event.hotelBookingUrl,
            item.event.optionGroups),
          // Note: invoice may be null...
          invoice: (item.invoice ? {
            ...item.invoice,
            date: item.invoice?.date
          } : null),
        })
        )
      }),
      tap((registrations: Registration[]) => { // The tap operator now receives the converted Registration[]
        this.cacheDirty = false;
      }),
        catchError(err => throwError(() => err))
    );
  }

  deleteRegistration(registrationId: number) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    return this.http.delete<GenericResponse>(`${this.endpoint}/registrations/${registrationId}`, { headers }).pipe(
      tap(response => console.debug('Response: ', response)),
      //      catchError(this.handleError<GenericResponse>('deleteRegistration'))
        catchError(err => throwError(() => err))
    );
  }

  createRegistration(registrationData: RegistrationCreateRequest) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    console.debug('createRegistration called with data:', registrationData);
    return this.http.post<GenericResponse>(`${this.endpoint}/registrations/new`, registrationData, { headers }).pipe(
      tap(response => console.debug('Response: ', response)),
    );
  }

  updateRegistration(rur: RegistrationUpdateRequest) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    return this.http.put<GenericResponse>(`${this.endpoint}/registrations/${rur.id}`, rur, { headers }).pipe(
      tap(response => console.debug('Response: ', response)),
        catchError(err => throwError(() => err))
    );
  }

  confirmAttendee(registrationId: number, attendeeId: number): Observable<ConfirmationResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const body = {};

    return this.http.post<ConfirmationResponse>(`${this.endpoint}/registrations/${registrationId}/attendees/${attendeeId}`,
      body, { headers }).pipe(
        tap(response => console.debug('confirmAttendee Response: ', response)),
        catchError(err => throwError(() => err))
      );
  }

  deleteAttendee(registrationId: number, attendeeId: number): Observable<GenericResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.delete<GenericResponse>(`${this.endpoint}/registrations/${registrationId}/attendees/${attendeeId}`,
      { headers }).pipe(
        tap(response => console.debug('deleteAttendee Response: ', response)),
        catchError(err => throwError(() => err))
      );
  }

}
